import * as React from 'react';
import { Viewport, ViewMedium, ViewSmall } from 'bernie-view';
import { UitkCard, UitkCardActionArea } from 'uitk-react-cards';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkLayoutFlex } from 'uitk-react-layout-flex';
import { UitkSpacing } from 'uitk-react-spacing';
import { classNames } from 'uitk-react-utils';
import { UitkIcon } from 'uitk-react-icons';
import { UitkHeading } from 'uitk-react-text';
import { CardLink, PlaceholderCards } from '../..';
import { ActivityDestinationViewSearch } from '../../../../../../common/__generated__/api';
import { equals } from '../../../../../../helpers/common-helper';
import { useStores } from '../../../../../../utils/hooks/useStores';
import { ActivitySpannedTileCardContent, ActivitySpannedTileCardFeatureHeader, ActivitySpannedTileCardImage } from '.';

export interface ActivitySpannedTileCardProps {
  activity: ActivityDestinationViewSearch.ActivityInlineFragment;
  loading: boolean;
  index: number;
  className?: string;
  onClick?: (activity: ActivityDestinationViewSearch.ActivityInlineFragment, index: number) => void;
  isRecommendationTile?: boolean | undefined;
  activityGroup?: string;
  titleLines?: number;
  isInCarousel?: boolean;
}

const ActivitySpannedTileCardComponent: React.FC<ActivitySpannedTileCardProps> = ({
  activity,
  className,
  loading,
  index,
  isRecommendationTile,
  onClick,
  activityGroup,
  isInCarousel
}): JSX.Element => {
  const { searchCriteriaStore, detailCriteriaStore } = useStores();
  const regionName = isRecommendationTile
    ? detailCriteriaStore.detailQuery.location
    : searchCriteriaStore.searchQuery.location;
  const regionId = isRecommendationTile ? detailCriteriaStore.detailQuery.rid : searchCriteriaStore.searchQuery.rid;
  const ActivitySpannedTileCardFeatureTileBottom = (): JSX.Element => (
    <>
      {activity?.features?.pinnedTopBottom?.tileBottom && (
        <UitkCardActionArea
          className="uitk-type-300 pinned-bottom"
          data-testid="activity-spanned-tile-card-pinned-top-bottom-tile-bottom"
        >
          {activity.features.pinnedTopBottom.tileBottom.description}
          <UitkIcon name={activity.features.pinnedTopBottom.tileBottom.id} />
        </UitkCardActionArea>
      )}
    </>
  );

  const renderActivityCard = (isVerticalLayout: boolean): JSX.Element => {
    if (isVerticalLayout) {
      return (
        <>
          <UitkSpacing margin={{ blockstart: 'two', blockend: 'one' }}>
            <UitkCard
              className={classNames('card-overflow-hidden full-width', `${className ? className : ''}`)}
              data-testid="activity-spanned-tile-card"
            >
              <ActivitySpannedTileCardFeatureHeader activity={activity} />
              <UitkLayoutFlex justifyContent="space-between" direction="column" className="max-height">
                <UitkHeading tag="h3" className="is-hidden" aria-hidden="false">
                  {activity.name}
                </UitkHeading>
                <ActivitySpannedTileCardImage
                  activity={activity}
                  activityGroup={activityGroup}
                  isVerticalLayout={isVerticalLayout}
                />
                <ActivitySpannedTileCardContent
                  activity={activity}
                  isVerticalLayout={isVerticalLayout}
                  className={className}
                />
                <ActivitySpannedTileCardFeatureTileBottom />
                <CardLink
                  activity={activity}
                  index={index}
                  regionName={regionName}
                  regionId={regionId}
                  onClick={onClick}
                  isRecommendationTile={isRecommendationTile ? isRecommendationTile : false}
                />
              </UitkLayoutFlex>
            </UitkCard>
          </UitkSpacing>
        </>
      );
    }

    return (
      <>
        <UitkSpacing margin={{ blockstart: 'two', blockend: 'one' }}>
          <UitkCard
            className={classNames('card-overflow-hidden full-width', `${className ? className : ''}`)}
            data-testid="activity-spanned-tile-card"
          >
            <UitkHeading tag="h3" className="is-hidden" aria-hidden="false">
              {activity.name}
            </UitkHeading>
            <ActivitySpannedTileCardFeatureHeader activity={activity} />
            <UitkLayoutGrid columns={{ small: 1, medium: isVerticalLayout ? 1 : 3 }}>
              <UitkLayoutGridItem colSpan={{ small: 1, medium: 1 }}>
                <ActivitySpannedTileCardImage
                  activity={activity}
                  activityGroup={activityGroup}
                  isVerticalLayout={isVerticalLayout}
                />
              </UitkLayoutGridItem>
              <ActivitySpannedTileCardContent
                activity={activity}
                isVerticalLayout={isVerticalLayout}
                className={className}
              />
            </UitkLayoutGrid>
            <ActivitySpannedTileCardFeatureTileBottom />
            <CardLink
              activity={activity}
              index={index}
              regionName={regionName}
              regionId={regionId}
              onClick={onClick}
              isRecommendationTile={isRecommendationTile ? isRecommendationTile : false}
            />
          </UitkCard>
        </UitkSpacing>
      </>
    );
  };

  if (loading) {
    return <PlaceholderCards />;
  }

  if (isInCarousel || isRecommendationTile) {
    return renderActivityCard(true);
  }

  return (
    <Viewport>
      <ViewSmall>{renderActivityCard(true)}</ViewSmall>
      <ViewMedium>{renderActivityCard(false)}</ViewMedium>
    </Viewport>
  );
};

export const ActivitySpannedTileCard = React.memo(ActivitySpannedTileCardComponent, (prevProps, nextProps): boolean => {
  let isActivitySame = Boolean(prevProps.activity) && Boolean(nextProps.activity);
  isActivitySame =
    isActivitySame ||
    (prevProps.activity && nextProps.activity && equals(prevProps.activity.id, nextProps.activity.id));

  return !isActivitySame;
});
